/**
 * Created by ivo on 31.01.17.
 */

import { useRouterHistory } from 'react-router';
import createBrowserHistory from 'history/lib/createBrowserHistory';
import {ModalUserConfirmation} from "./lib/history";
import _ from 'underscore';

import Log from './Log';
import CONFIG from './config';
import {NotificationActions} from './services/Notification';
import t from './trans';
import LocationInfo from "./LocationInfo";
import {NavigationStore} from "./services/Navigation";
import {InternalErrorException} from "./Exceptions";

const browserHistory = useRouterHistory(createBrowserHistory)({
    basename: CONFIG.baseUrl,
    /**
     * We use custom navigation confirmation dialog, mounted into div with the id "modal-holder". This div should be
     * manually created in main index.html file!
     */
    getUserConfirmation: ModalUserConfirmation("modal-holder")
});

const UI_BASE = "/ui";

class Navigation {

    static authFailed() {
        NotificationActions.error(t("Session expired, please log in"));
        //UserActions.logout.defer();
        Navigation.gotoLogin();
    }

    static authFailedDefer() {
        NotificationActions.error.defer(t("Session expired, please log in"));
        //UserActions.logout.defer();
        Navigation.gotoLogin();
    }

    static gotoMain() {
        Navigation.push('/');
    }

    static gotoUi() {
        Navigation.push(UI_BASE);
    }

    static gotoForgotPasswordPage() {
        Navigation.push("/forgotPassword");
    }

    static gotoAuth(nonce, redirect) {

        Navigation.push({
            pathname: '/auth',
            query: {
                client_id: CONFIG.applicationCode,
                redirect_url: CONFIG.redirectUrl,
                state: nonce
            },
            state: {
                redirect: redirect
            }
        });
    }

    static gotoLogin(redirect) {
        if (redirect) Navigation.push({
            pathname: '/login',
            state: {redirect: redirect}
            });
        else Navigation.push('/login');
    }

    static gotoViewResource(resource, id) {
        Navigation.push(UI_BASE + "/" + resource + "/" + id);
    }

    static replaceViewResource(resource, id) {
        Navigation.replace(UI_BASE + "/" + resource + "/" + id);
    }

    static replaceSearchResource(resource, query) {
        Navigation.replace({
            pathname: UI_BASE + "/" + resource,
            query: query
        });
    }

    /**
     *
     * @param {LocationProps} locationProps
     */
    static gotoResourceAction(locationProps) {
        Navigation.push({
            pathname: UI_BASE + LocationInfo.makeResourceUrl(locationProps),
            query: locationProps.query
        });
    }

    /**
     * Navigates back in location history until browser is outside of specified resource pages.
     * If this is not possible (stack does not contain pages outside of specified resource) then navigate up
     * until borwser is otuside of specified resource pages.
     * If this is also not possible then replace current page to the search page of specified resource.
     */
    static replaceBackOutsideResource() {
        let navigationHistory = Navigation.getHistoryStack();
        if (!navigationHistory || navigationHistory.length == 0) throw InternalErrorException("Internal error");

        let currentLocation = navigationHistory[navigationHistory.length-1];
        let currentLocationInfo = new LocationInfo(UI_BASE, currentLocation.basename + currentLocation.pathname + currentLocation.hash);
        let currentLocationParams = currentLocationInfo.getLocationProps();

        for (let pos = navigationHistory.length-2; pos >= 0; pos--) {
            let locationInfo = new LocationInfo(UI_BASE, navigationHistory[pos].basename + navigationHistory[pos].pathname + navigationHistory[pos].hash);
            let locationParams = locationInfo.getLocationProps();

            if (currentLocationParams.resource !== locationParams.resource || currentLocationParams.id !== locationParams.id) {
                // Location position where to go!
                Navigation.gotoBack(navigationHistory.length - pos - 1);
                return;
            }
        }

        // History does not contain suitable position. Navigate up:
        let locationStack = currentLocationInfo.getLocationStack().slice();
        let stackLen = locationStack.length;
        let last = locationStack.pop();
        while (last && last.resource === currentLocationParams.resource && last.id === currentLocationParams.id) last = locationStack.pop();
        if (last) {
            // Go to this path:
            Navigation.replace(last.path);
        } else {
            // There were no unrelated pages. We will navigate directly into resource listing page:
            Navigation.replaceUiUrl("/" + currentLocationParams.resource);
        }
    }

    /**
     * Navigates back to the previous page. If there is no previous page then navigate up one level.
     */
    static gotoBackOrUp(steps = 1) {
        let navigationHistory = Navigation.getHistoryStack();
        if (navigationHistory.length > steps) Navigation.gotoBack(steps);
        else Navigation.gotoUp(steps);
    }

    /**
     * Navigates up on the hierarhy
     * @param {number} steps Number of steps. If this is greater than hierarhy depth then top level will be used
     */
    static gotoUp(steps) {
        let navigationHistory = Navigation.getHistoryStack();
        if (!navigationHistory || navigationHistory.length == 0) throw new InternalErrorException("Internal error");

        let currentLocation = navigationHistory[navigationHistory.length-1];
        let currentLocationInfo = new LocationInfo(UI_BASE, currentLocation.basename + currentLocation.pathname + currentLocation.hash);

        let locationStack = currentLocationInfo.getLocationStack();
        if (locationStack.length > steps) {
            Navigation.push(locationStack[locationStack.length - steps - 1].path);
        } else {
            Navigation.gotoUi();
        }
    }



    static getHistory() {
        return browserHistory;
    }

    static gotoUiUrl(url) {
        if (!url.startsWith("/")) Navigation.push(url);
        else Navigation.push(UI_BASE + url);
    }

    static replaceUiUrl(url) {
        Navigation.replace(UI_BASE + url);
    }

    static gotoExtUrl(url) {
        Log.debug("Navigating to external URL:", url);
        window.location.href = url;
    }

    static push(location, state) {
        if (_.isString(location)) Log.debug("Navigating to (push):", location);
        else Log.debug("Navigating to (push):", location.pathname);
        Navigation.getHistory().push(location, state);
    }

    static replace(location, state) {
        if (_.isString(location)) Log.debug("Navigating to (replace):", location);
        else Log.debug("Navigating to (replace):", location.pathname);
        Navigation.getHistory().replace(location, state);
    }

    static gotoBack(steps = 1) {
        Navigation.getHistory().go(-steps);
    }

    static isHistoryStackEmpty() {
        return NavigationStore.isHistoryEmpty();
    }

    static getHistoryStack() {
        return NavigationStore.getHistory();
    }
}

export default Navigation;
